"use client"

import React, {useState, useCallback, useRef, useEffect, useId} from "react"
import {FilterSkeleton} from "@/app/components/auctor/filter/Filter"
import {
    DEFAULT_LOAN_AMOUNT,
    DEFAULT_LOAN_YEAR_DURATION,
    DEFAULT_BORROWER_AGE,
} from "@/app/(pages)/taux-pret-immobilier/utils"
import AppSlider from "@/app/components/atomic/AppSlider"
import AppSelect from "@/app/components/atomic/AppSelect"
import { REGION_OPTIONS } from "@/app/constants/loanRegions"

interface LoanRateComparatorFormProps {
    onInputChange: (loanAmount: number, loanDuration: number, borrowerAge: number, region: string) => Promise<void>
    selectedRegion?: string
    onRegionChange?: (region: string) => void
}

export default function LoanRateComparatorForm({onInputChange, selectedRegion = "", onRegionChange}: LoanRateComparatorFormProps) {
    const [loanAmount, setLoanAmount] = useState(DEFAULT_LOAN_AMOUNT)
    const [loanDuration, setLoanDuration] = useState(DEFAULT_LOAN_YEAR_DURATION)
    const [borrowerAge, setBorrowerAge] = useState(DEFAULT_BORROWER_AGE)

    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
        }
    }, [])

    const createSliderHandler = useCallback((fieldName: "loanAmount" | "loanDuration" | "borrowerAge") => {
        return (value: number) => {
            let newLoanAmount = loanAmount
            let newLoanDuration = loanDuration
            let newBorrowerAge = borrowerAge

            switch (fieldName) {
                case "loanAmount":
                    newLoanAmount = value
                    setLoanAmount(newLoanAmount)
                    break
                case "loanDuration":
                    newLoanDuration = Math.round(value)
                    setLoanDuration(newLoanDuration)
                    break
                case "borrowerAge":
                    newBorrowerAge = Math.round(value)
                    setBorrowerAge(newBorrowerAge)
                    break
            }

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }

            timeoutRef.current = setTimeout(() => {
                onInputChange(newLoanAmount, newLoanDuration, newBorrowerAge, selectedRegion)
            }, 300)
        }
    }, [onInputChange, loanAmount, loanDuration, borrowerAge, selectedRegion])

    const handleRegionChange = (region: string) => {
        onRegionChange?.(region)
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        onInputChange(loanAmount, loanDuration, borrowerAge, region)
    }

    return (
        <form className="grid gap-6 p-6">
            {onRegionChange && (
                <div className="flex flex-col gap-3" data-test="region-input">
                    <AppSelect
                        label="Région"
                        variant="v2"
                        searchable
                        options={REGION_OPTIONS}
                        value={selectedRegion}
                        onChange={(e) => handleRegionChange(String(e.target.value))}
                    />
                </div>
            )}

            <FormField
                label="Durée du prêt"
                value={`${loanDuration} ans`}
                dataTest="loan-duration-input"
            >
                <AppSlider
                    minValue={5}
                    maxValue={25}
                    value={loanDuration}
                    step={5}
                    showMarkers
                    showMedianValue={false}
                    handleChange={createSliderHandler("loanDuration")}
                />
            </FormField>

            <FormField
                label="Montant du prêt"
                value={`${loanAmount.toLocaleString("fr-FR")} €`}
                dataTest="loan-amount-input"
            >
                <AppSlider
                    minValue={50000}
                    maxValue={1000000}
                    value={loanAmount}
                    step={25000}
                    showMarkers
                    showMedianValue
                    handleChange={createSliderHandler("loanAmount")}
                />
            </FormField>

            <FormField
                label="Âge de l'emprunteur"
                value={`${borrowerAge} ans`}
                dataTest="borrower-age-input"
            >
                <AppSlider
                    minValue={18}
                    maxValue={75}
                    value={borrowerAge}
                    step={1}
                    showMarkers
                    showMedianValue={false}
                    handleChange={createSliderHandler("borrowerAge")}
                />
            </FormField>
        </form>
    )
}

function FormField({
                       label,
                       value,
                       dataTest,
                       children,
                   }: {
    label: string
    value: string
    dataTest: string
    children: React.ReactElement<{id?: string}>
}) {
    const sliderId = useId()
    return (
        <div className="flex flex-col gap-3" data-test={dataTest}>
            <div className="flex justify-between items-center">
                <label htmlFor={sliderId} className="text-pale font-semibold">{label}</label>
                <span className="text-rm-blue font-semibold">{value}</span>
            </div>
            {React.cloneElement(children, {id: sliderId})}
        </div>
    )
}

export function LoanRateComparatorFormSkeleton({filtersCount}: { filtersCount: number }) {
    return (
        <div className="grid gap-6 p-6">
            {Array.from(Array(filtersCount).keys()).map((idx: number) => (
                <FilterSkeleton key={idx} />
            ))}
        </div>
    )
}
